import isEmpty from "lodash/isEmpty";
import isEqual from "lodash/isEqual";
import sortBy from "lodash/sortBy";
import xor from "lodash/xor";
import { ChangelogItem } from "PFApp/components/changelog/components/changelog_item";
import { Stack } from "PFComponents/containers";
import { CustomFieldsChanges } from "PFCore/types/changelog";
import { Importance } from "PFTypes";

import { CustomFieldChangeGroup } from "./custom_field_change_group/custom_field_change_group";

type CustomFieldsChangelogProps = {
  customFields: CustomFieldsChanges;
};

export const CustomFieldsChangelog = ({ customFields }: CustomFieldsChangelogProps) => (
  <Stack>
    {Object.entries(customFields).map(([fieldKey, { before = [], after = [], matchable, rankable }]) => {
      const unchangedIds = new Set(
        before
          .filter((beforeItem) => after.some((afterItem) => isEqual(beforeItem, afterItem)))
          .map((item) => item.customValueId)
      );

      const filteredBefore = before.filter((item) => !unchangedIds.has(item.customValueId));
      const filteredAfter = after.filter((item) => !unchangedIds.has(item.customValueId));

      if (isEmpty(filteredBefore) && isEmpty(filteredAfter)) {
        return null;
      }

      const beforeIds = new Set(filteredBefore.map((item) => item.customValueId));
      const afterIds = new Set(filteredAfter.map((item) => item.customValueId));
      const removedIds = new Set([...beforeIds].filter((id) => !afterIds.has(id)));
      const addedIds = new Set([...afterIds].filter((id) => !beforeIds.has(id)));

      const changedExperienceIds = new Set(
        filteredAfter
          .filter((afterItem) => {
            const beforeItem = filteredBefore.find((item) => item.customValueId === afterItem.customValueId);
            return beforeItem && beforeItem.requiredExperience !== afterItem.requiredExperience;
          })
          .map((item) => item.customValueId)
      );

      const highlightedImportances = new Set<Importance>(
        (Object.values(Importance) as Importance[]).filter((importance) => {
          const afterIdsForImportance = after
            .filter((item) => item.importance === importance)
            .map((item) => item.customValueId);
          const beforeIdsForImportance = before
            .filter((item) => item.importance === importance)
            .map((item) => item.customValueId);
          return xor(afterIdsForImportance, beforeIdsForImportance).length > 0;
        })
      );

      const isImportanceHighlighted = (importance: Importance) => highlightedImportances.has(importance);

      const sortedBefore = sortBy(filteredBefore, (item) => removedIds.has(item.customValueId));
      const sortedAfter = sortBy(filteredAfter, (item) => addedIds.has(item.customValueId));

      return (
        <ChangelogItem
          key={fieldKey}
          before={
            sortedBefore.length ? (
              <CustomFieldChangeGroup
                values={sortedBefore}
                removedIds={removedIds}
                addedIds={addedIds}
                matchable={matchable}
                rankable={rankable}
                isAfterBlock={false}
              />
            ) : null
          }
          after={
            sortedAfter.length ? (
              <CustomFieldChangeGroup
                values={sortedAfter}
                removedIds={removedIds}
                addedIds={addedIds}
                matchable={matchable}
                rankable={rankable}
                changedExperienceIds={changedExperienceIds}
                isImportanceHighlighted={isImportanceHighlighted}
                isAfterBlock
              />
            ) : null
          }
        />
      );
    })}
  </Stack>
);
